import { useMemo, useState } from "react";
import { ChevronDown, Search, Star } from "lucide-react";
import { agruparPorCategoria } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { useFavoritos } from "@/lib/use-favoritos";
import { SingleSelectButtons } from "./SingleSelectButtons";

/** A partir daqui a lista ganha busca — com poucos serviços, a busca só ocupa espaço. */
const COM_BUSCA = 12;

const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * Escolha do serviço no pedido: os serviços ativos do laboratório separados pela categoria, na
 * ordem da base — no mesmo desenho do seletor de cores. Cada categoria vem fechada e o cabeçalho
 * mostra o serviço já escolhido; escolher fecha de volta. Com busca, as categorias com resultado
 * abrem sozinhas, e laboratório com uma categoria só já mostra os botões. Os favoritos da pessoa
 * (estrela) ficam num grupo aberto no topo.
 */
export function ServicosAgrupados({
  servicos,
  selectedId,
  onSelect,
  originalId,
  disabled,
}: {
  servicos: { id: string; label: string; group: string | null }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  originalId?: string | null;
  disabled?: boolean;
}) {
  const [busca, setBusca] = useState("");
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const favoritos = useFavoritos("protese");
  const buscando = !!busca.trim();
  const achados = useMemo(() => {
    const termo = semAcento(busca.trim());
    return termo ? servicos.filter((s) => semAcento(s.label).includes(termo)) : servicos;
  }, [servicos, busca]);
  const grupos = useMemo(() => agruparPorCategoria(achados), [achados]);
  const favoritosAchados = achados.filter((s) => favoritos.ids.has(s.id));
  const botoes = { favoritos: favoritos.ids, onFavoritar: favoritos.alternar };

  const alternar = (categoria: string, abrir?: boolean) =>
    setAbertas((prev) => {
      const next = new Set(prev);
      if (abrir ?? !next.has(categoria)) next.add(categoria);
      else next.delete(categoria);
      return next;
    });

  return (
    <div className="space-y-2">
      {servicos.length > COM_BUSCA && (
        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar serviço"
            aria-label="Buscar serviço"
            className="h-8 w-full rounded-md border border-zinc-200 bg-white pl-8 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          />
        </label>
      )}
      {grupos.length === 0 && (
        <p className="text-xs text-zinc-400 dark:text-muted-foreground">
          {buscando ? "Nenhum serviço com essa busca." : "O laboratório ainda não tem serviços ativos."}
        </p>
      )}
      {favoritosAchados.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-500/30">
          <p className="flex items-center gap-1.5 px-3 pt-2 text-[12px] font-medium text-zinc-700 dark:text-foreground/80">
            <Star className="h-3 w-3 text-amber-500" fill="currentColor" />
            Favoritos
          </p>
          <div className="px-3 py-2">
            <SingleSelectButtons
              options={favoritosAchados.map((s) => ({ id: s.id, label: s.label }))}
              selectedId={selectedId}
              originalId={originalId}
              disabled={disabled}
              {...botoes}
              onSelect={(id) => {
                onSelect(id);
                setBusca("");
              }}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {grupos.map(({ categoria, itens }) => {
        const aberta = buscando || grupos.length === 1 || abertas.has(categoria);
        const escolhido = itens.find((s) => s.id === selectedId);
        return (
          // Aberta, a categoria toma a linha inteira: os botões precisam da largura toda.
          <div key={categoria} className={cn("rounded-lg border border-zinc-200 dark:border-border", aberta && "sm:col-span-2")}>
            <button
              type="button"
              onClick={() => alternar(categoria)}
              aria-expanded={aberta}
              title={aberta ? "Ocultar" : escolhido ? "Trocar" : "Escolher"}
              className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/40"
            >
              <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-zinc-700 dark:text-foreground/80">
                {categoria}
                <span className="ml-1.5 text-[11px] font-normal text-zinc-400 dark:text-muted-foreground">({itens.length})</span>
              </span>
              {escolhido && (
                <span className="max-w-[45%] shrink-0 truncate rounded-md bg-brand-50 px-2 py-0.5 text-[12px] font-semibold text-brand-700 dark:bg-primary/15 dark:text-primary">
                  {escolhido.label}
                </span>
              )}
              <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform", aberta && "rotate-180")} />
            </button>

            {aberta && (
              <div className="border-t border-zinc-100 px-3 py-2 dark:border-border/60">
                <SingleSelectButtons
                  options={itens.map((s) => ({ id: s.id, label: s.label }))}
                  selectedId={selectedId}
                  originalId={originalId}
                  disabled={disabled}
                  {...botoes}
                  onSelect={(id) => {
                    onSelect(id);
                    if (id !== selectedId) {
                      alternar(categoria, false);
                      setBusca("");
                    }
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
